import { useState } from 'react';
import PropertyDiagnosisForm from './PropertyDiagnosisForm';

function PricingSection() {
  const [activeSimTab, setActiveSimTab] = useState(1);

  const simPatterns = [
    {
      label: '月30万円',
      monthlyRevenue: '¥300,000',
      feeMin: '¥30,000',
      feeMax: '¥45,000',
      netMin: '¥255,000',
      netMax: '¥270,000',
      annualMin: '¥3,060,000',
      annualMax: '¥3,240,000',
      tag: 'スモールスタート',
      tagColor: 'bg-slate-100 text-slate-600',
      barWidth: '20%',
    },
    {
      label: '月50万円',
      monthlyRevenue: '¥500,000',
      feeMin: '¥50,000',
      feeMax: '¥75,000',
      netMin: '¥425,000',
      netMax: '¥450,000',
      annualMin: '¥5,100,000',
      annualMax: '¥5,400,000',
      tag: '標準モデル',
      tagColor: 'bg-emerald-100 text-emerald-700',
      barWidth: '33%',
    },
    {
      label: '月100万円',
      monthlyRevenue: '¥1,000,000',
      feeMin: '¥100,000',
      feeMax: '¥150,000',
      netMin: '¥850,000',
      netMax: '¥900,000',
      annualMin: '¥10,200,000',
      annualMax: '¥10,800,000',
      tag: 'フル稼働モデル',
      tagColor: 'bg-amber-100 text-amber-700',
      barWidth: '67%',
    },
    {
      label: '月150万円',
      monthlyRevenue: '¥1,500,000',
      feeMin: '¥150,000',
      feeMax: '¥225,000',
      netMin: '¥1,275,000',
      netMax: '¥1,350,000',
      annualMin: '¥15,300,000',
      annualMax: '¥16,200,000',
      tag: '高収益モデル',
      tagColor: 'bg-rose-100 text-rose-600',
      barWidth: '100%',
    },
  ];

  const mainPlans = [
    {
      name: 'Launch Support',
      nameJa: '開業サポート',
      price: '¥98,000〜',
      description: 'Perfect for first-time hosts ready to launch',
      targetAudience: '初めて民泊を始める方、申請手続きのサポートが必要な方',
      priceBreakdown: '申請書類作成・保健所対応・Airbnb初期設定を含む',
      includes: [
        '簡易宿所申請サポート',
        '民泊届出サポート',
        '保健所・消防アドバイス',
        'Airbnbページ立ち上げ支援',
        '初期価格戦略設計'
      ],
      featured: false
    },
    {
      name: 'Full Setup & Launch',
      nameJa: '開業＋運営設計',
      price: '¥198,000〜',
      description: 'Complete solution from property selection to launch',
      targetAudience: '物件選びから本格的に始めたい方、プロの運営体制を構築したい方',
      priceBreakdown: '物件選定・申請・写真撮影・清掃体制構築・収益設計まで全てサポート',
      includes: [
        '物件選定アドバイス',
        '申請サポート',
        '写真ディレクション',
        'Airbnb最適化',
        '清掃体制構築',
        '収益モデル設計'
      ],
      featured: true,
      badge: '人気No.1'
    },
    {
      name: 'Management Service',
      nameJa: '運営代行',
      price: '10%〜15%',
      priceNote: '月間売上より',
      description: 'Hands-off operation with expert management',
      targetAudience: '運営を完全にお任せしたい方、本業に集中したいオーナー様',
      priceBreakdown: '月間売上の10〜15%（物件規模・サービス内容により変動）',
      includes: [
        '価格調整',
        'ゲスト対応',
        'レビュー管理',
        '売上改善提案',
        '月次レポート'
      ],
      featured: false
    }
  ];

  const additionalServices = [
    {
      name: 'Cleaning Service',
      nameJa: '清掃サービス',
      price: '¥6,000〜',
      priceNote: '1回あたり',
      description: '物件規模による',
      details: '1R〜1LDK: ¥6,000 / 2LDK〜3LDK: ¥9,000 / 4LDK以上: ¥12,000'
    },
    {
      name: 'Accounting Support',
      nameJa: '経理・確定申告サポート',
      price: 'ご相談',
      priceNote: '',
      description: 'Tax filing support system available',
      details: '提携税理士事務所による確定申告サポート（別途お見積もり）'
    },
    {
      name: 'Photography',
      nameJa: 'プロ撮影',
      price: '¥19,800〜',
      priceNote: '',
      description: 'Professional listing photos',
      details: '物件撮影・編集・Airbnb掲載用画像作成'
    },
    {
      name: 'Interior Consultation',
      nameJa: 'インテリアコンサル',
      price: '¥29,800〜',
      priceNote: '',
      description: 'Design advice for higher bookings',
      details: '家具選定・レイアウト提案・購入サポート'
    }
  ];

  const activeSim = simPatterns[activeSimTab];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Plans */}
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold text-slate-900 mb-6">料金プラン</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            まずは気軽にご相談ください 😊<br />
            あなたの状況に合わせて、一緒に最適なプランを考えます。
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {mainPlans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-3xl p-10 transition-all duration-300 hover:scale-[1.02] cursor-pointer ${
                plan.featured
                  ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-2xl ring-4 ring-emerald-500/20'
                  : 'bg-white border-2 border-slate-200 hover:border-emerald-500 hover:shadow-2xl'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 text-sm font-bold rounded-full shadow-lg whitespace-nowrap">
                  {plan.badge}
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className={`text-3xl font-bold mb-3 ${plan.featured ? 'text-white' : 'text-slate-900'}`}>
                  {plan.nameJa}
                </h3>
                <p className={`text-sm mb-6 ${plan.featured ? 'text-emerald-50' : 'text-slate-500'}`}>
                  {plan.name}
                </p>
                <div className={`text-5xl font-bold mb-2 ${plan.featured ? 'text-white' : 'text-emerald-600'}`}>
                  {plan.price}
                </div>
                {plan.priceNote && (
                  <p className={`text-sm mb-4 ${plan.featured ? 'text-emerald-50' : 'text-slate-500'}`}>
                    {plan.priceNote}
                  </p>
                )}
              </div>

              {/* Target Audience */}
              <div className={`mb-6 p-4 rounded-xl ${
                plan.featured ? 'bg-white/10 backdrop-blur-sm' : 'bg-emerald-50'
              }`}>
                <p className={`text-xs font-semibold mb-2 ${
                  plan.featured ? 'text-emerald-100' : 'text-emerald-700'
                }`}>
                  こんな方におすすめ
                </p>
                <p className={`text-sm leading-relaxed ${
                  plan.featured ? 'text-white' : 'text-slate-700'
                }`}>
                  {plan.targetAudience}
                </p>
              </div>

              {/* Price Breakdown */}
              <div className="mb-6">
                <p className={`text-xs font-semibold mb-2 ${
                  plan.featured ? 'text-emerald-100' : 'text-slate-500'
                }`}>
                  料金に含まれるもの
                </p>
                <p className={`text-sm leading-relaxed ${
                  plan.featured ? 'text-emerald-50' : 'text-slate-600'
                }`}>
                  {plan.priceBreakdown}
                </p>
              </div>

              {/* Includes */}
              <div className="space-y-3 mb-8">
                {plan.includes.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <i className={`ri-checkbox-circle-fill text-xl flex-shrink-0 mt-0.5 ${
                      plan.featured ? 'text-white' : 'text-emerald-500'
                    }`}></i>
                    <span className={`text-sm leading-relaxed ${plan.featured ? 'text-white' : 'text-slate-700'}`}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <a
                href="/for-owners#contact"
                className={`block w-full text-center px-6 py-4 rounded-xl font-bold transition-all duration-300 whitespace-nowrap ${
                  plan.featured
                    ? 'bg-white text-emerald-600 hover:bg-emerald-50 shadow-lg hover:shadow-xl'
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:shadow-xl hover:scale-105'
                }`}
              >
                無料相談を予約する
              </a>
            </div>
          ))}
        </div>

        {/* ── 収益シミュレーション ── */}
        <div className="mb-32 bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-bar-chart-2-fill text-emerald-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="text-emerald-400 text-sm font-semibold tracking-wide uppercase">Revenue Simulation</span>
              </div>
              <h3 className="text-2xl font-bold text-white">収益シミュレーション</h3>
              <p className="text-slate-400 text-sm mt-1">運営代行プラン（10〜15%）を利用した場合の収益例</p>
            </div>
            {/* Tab switcher */}
            <div className="flex items-center gap-1 bg-slate-700/60 rounded-full p-1">
              {simPatterns.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setActiveSimTab(i)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSimTab === i
                      ? 'bg-emerald-500 text-white shadow-md'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="px-10 py-10">
            {/* Tag */}
            <div className="flex items-center gap-3 mb-8">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${activeSim.tagColor}`}>
                {activeSim.tag}
              </span>
              <span className="text-slate-400 text-sm">月間売上 {activeSim.monthlyRevenue} の場合</span>
              {activeSimTab === 3 && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-rose-500 to-amber-500 text-white shadow-sm">
                  🔥 複数物件・高稼働率
                </span>
              )}
            </div>

            {/* Main numbers */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {/* 月間売上 */}
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">月間売上</p>
                <p className="text-4xl font-bold text-slate-900">{activeSim.monthlyRevenue}</p>
                <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-slate-400 rounded-full transition-all duration-500"
                    style={{ width: activeSim.barWidth }}
                  ></div>
                </div>
              </div>

              {/* 代行費用 */}
              <div className="bg-rose-50 rounded-2xl p-6 border border-rose-100">
                <p className="text-xs font-semibold text-rose-400 mb-2 uppercase tracking-wide">運営代行費用（10〜15%）</p>
                <p className="text-4xl font-bold text-rose-500">
                  -{activeSim.feeMin}
                </p>
                <p className="text-sm text-rose-400 mt-1">〜 -{activeSim.feeMax}</p>
                <div className="mt-4 h-2 bg-rose-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-rose-300 rounded-full transition-all duration-500"
                    style={{ width: '15%' }}
                  ></div>
                </div>
              </div>

              {/* 手取り */}
              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200 ring-2 ring-emerald-400/30">
                <p className="text-xs font-semibold text-emerald-600 mb-2 uppercase tracking-wide">オーナー様の手取り</p>
                <p className="text-4xl font-bold text-emerald-600">
                  {activeSim.netMin}
                </p>
                <p className="text-sm text-emerald-500 mt-1">〜 {activeSim.netMax}</p>
                <div className="mt-4 h-2 bg-emerald-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                    style={{ width: '85%' }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Annual projection */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-calendar-2-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-emerald-100 text-sm font-medium">年間換算の手取り収益（概算）</p>
                  <p className="text-white text-xs mt-0.5">12ヶ月継続した場合の目安</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white text-3xl font-bold">{activeSim.annualMin}</p>
                <p className="text-emerald-100 text-sm">〜 {activeSim.annualMax}</p>
              </div>
            </div>

            {/* Comparison bar */}
            <div className="mt-8">
              <p className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wide">4パターン比較</p>
              <div className="space-y-3">
                {simPatterns.map((p, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 cursor-pointer group`}
                    onClick={() => setActiveSimTab(i)}
                  >
                    <span className={`text-sm font-semibold w-20 shrink-0 ${activeSimTab === i ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {p.label}
                    </span>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          i === 3
                            ? activeSimTab === i
                              ? 'bg-gradient-to-r from-rose-500 to-amber-400'
                              : 'bg-gradient-to-r from-rose-300 to-amber-200 group-hover:from-rose-400 group-hover:to-amber-300'
                            : activeSimTab === i
                            ? 'bg-emerald-500'
                            : 'bg-slate-300 group-hover:bg-emerald-300'
                        }`}
                        style={{ width: p.barWidth }}
                      ></div>
                    </div>
                    <span className={`text-sm font-bold w-32 text-right shrink-0 ${
                      i === 3
                        ? activeSimTab === i ? 'text-rose-500' : 'text-slate-400'
                        : activeSimTab === i ? 'text-emerald-600' : 'text-slate-400'
                    }`}>
                      {p.netMin}〜
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-slate-400 mt-6">
              ※ 上記はあくまで概算シミュレーションです。実際の収益は物件・立地・稼働率・季節等により異なります。
            </p>

            {/* CTA after simulation */}
            <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/20 rounded-xl shrink-0">
                  <i className="ri-calendar-check-line text-emerald-400 text-2xl"></i>
                </div>
                <div>
                  <p className="text-white text-lg font-bold mb-1">あなたの物件でシミュレーションしてみませんか？</p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    実際の物件情報をもとに、より精度の高い収益予測をご提案します。<br className="hidden md:block" />
                    無料相談は30分・完全オンライン対応です。
                  </p>
                </div>
              </div>
              <a
                href="/for-owners#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-105 whitespace-nowrap shrink-0"
              >
                <i className="ri-calendar-2-line text-xl"></i>
                <span>無料相談を予約する</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>

            {/* ── 物件収益診断フォーム ── */}
            <PropertyDiagnosisForm />
          </div>
        </div>

        {/* Additional Services - Table Format */}
        <div className="border-t-2 border-slate-200 pt-20">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-slate-900 mb-4">追加オプションサービス</h3>
            <p className="text-lg text-slate-600">運営をさらに充実させるオプションメニュー</p>
          </div>

          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
                    <th className="px-8 py-5 text-left text-sm font-bold text-slate-700 border-b-2 border-slate-200">
                      サービス名
                    </th>
                    <th className="px-8 py-5 text-left text-sm font-bold text-slate-700 border-b-2 border-slate-200">
                      料金
                    </th>
                    <th className="px-8 py-5 text-left text-sm font-bold text-slate-700 border-b-2 border-slate-200">
                      詳細
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {additionalServices.map((service, index) => (
                    <tr
                      key={index}
                      className="hover:bg-emerald-50/50 transition-colors duration-200 border-b border-slate-100 last:border-b-0"
                    >
                      <td className="px-8 py-6">
                        <div>
                          <h4 className="text-lg font-bold text-slate-900 mb-1">{service.nameJa}</h4>
                          <p className="text-sm text-slate-500">{service.name}</p>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div>
                          <span className="text-2xl font-bold text-emerald-600">{service.price}</span>
                          {service.priceNote && (
                            <p className="text-xs text-slate-500 mt-1">{service.priceNote}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-sm text-slate-700 leading-relaxed">{service.details}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-600 mb-6">
              オプションサービスの詳細や組み合わせについては、お気軽にご相談ください
            </p>
            <a
              href="/for-owners#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <span>詳しく相談する</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PricingSection;